import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import { OpenSheetMusicDisplay, TransposeCalculator } from 'opensheetmusicdisplay';
import { OSMDRenderCoordinator } from '../utils/osmdRenderCoordinator';
import RenderTimer from './RenderTimer';
import '../styles/score-display.css';

// The first note a part sings from a bar on (any part's, when `part` is
// null): the notes drawn at the earliest time in the first bar it sings in
function findCueNotes(graphic, part, fromMeasure) {
  const wanted = (graphicalNote) => {
    const note = graphicalNote?.sourceNote;
    if (!note || note.isRestFlag || !graphicalNote.PositionAndShape) return false;
    return part === null || String(note.ParentStaff?.ParentInstrument?.Id) === String(part);
  };
  for (const row of graphic?.measureList || []) {
    const number = row.find(Boolean)?.parentSourceMeasure?.MeasureNumber;
    if (fromMeasure !== null && number < fromMeasure) continue;
    let earliest = null;
    let found = [];
    row.forEach(measure => measure?.staffEntries?.forEach((entry) => {
      const when = entry.relInMeasureTimestamp?.RealValue ?? 0;
      entry.graphicalVoiceEntries?.forEach(voice => voice.notes?.forEach((graphicalNote) => {
        if (!wanted(graphicalNote)) return;
        if (earliest === null || when < earliest - 1e-9) {
          earliest = when;
          found = [graphicalNote];
        } else if (Math.abs(when - earliest) < 1e-9) {
          found.push(graphicalNote);
        }
      }));
    }));
    if (found.length) return found;
  }
  return [];
}

// `cue` ({ part, fromMeasure } or null): outside hover-to-hear, the first
// note of my part (from the highlighted section's first bar) still plays
// when hovered, for a starting pitch
const ScoreDisplay = forwardRef(({ onOsmdReady, zoom = 1.0, isMousePlayMode, currentFileContent, onPlayNote, partControls, cue = null }, ref) => {
  const osmdContainerRef = useRef(null);
  const osmdRef = useRef(null);
  const renderCoordinatorRef = useRef(null);
  const [timerState, setTimerState] = useState({
    isActive: false,
    timeRemaining: 0,
    progress: 0
  });
  
  // State for tooltip
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, note: '' });
  
  // State to track the last played note to prevent rapid repeated playing
  const lastPlayedNoteRef = useRef(null);
  // The cue's notes, found again when the cue or the drawn score changes
  const cueCacheRef = useRef({ key: null, graphic: null, notes: [] });
  
  // State for note highlighting
  const [highlightedNote, setHighlightedNote] = useState(null);

  // Helper function to get key signature from raw XML
  const getKeySignatureFromXML = useCallback(() => {
    if (!currentFileContent) {
      return null;
    }
    
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(currentFileContent, 'application/xml');
      
      // Look for the first key signature in the XML
      const keyElements = xmlDoc.querySelectorAll('key');
      
      for (const keyElement of keyElements) {
        const fifthsElement = keyElement.querySelector('fifths');
        if (fifthsElement) {
          const fifths = parseInt(fifthsElement.textContent);
          return fifths;
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }, [currentFileContent]);

  // Helper function to get key signature information (fifths value)
  const getKeySignatureFifths = useCallback((sourceNote) => {
    // First try direct XML parsing (most reliable)
    let fifths = getKeySignatureFromXML();
    
    if (fifths !== null) {
      return fifths;
    }
    
    // Fallback: Try to find the key signature fifths value from various OSMD paths
    // Method 1: Check SourceMeasure for key instructions
    if (sourceNote.SourceMeasure?.KeyInstructions && sourceNote.SourceMeasure.KeyInstructions.length > 0) {
      const keyInst = sourceNote.SourceMeasure.KeyInstructions[sourceNote.SourceMeasure.KeyInstructions.length - 1];
      if (keyInst && typeof keyInst.Key !== 'undefined') {
        return keyInst.Key;
      }
    }
    
    // Method 2: Check ParentStaff for key instructions
    if (sourceNote.ParentStaff?.KeyInstructions && sourceNote.ParentStaff.KeyInstructions.length > 0) {
      const keyInst = sourceNote.ParentStaff.KeyInstructions[sourceNote.ParentStaff.KeyInstructions.length - 1];
      if (keyInst && typeof keyInst.Key !== 'undefined') {
        return keyInst.Key;
      }
    }
    
    return null;
  }, [getKeySignatureFromXML]);

  // Helper function to spell note correctly based on key signature fifths
  const getCorrectNoteSpelling = useCallback((chromaticIndex, fifths) => {
    // Default chromatic notes (using sharps)
    const defaultNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    
    if (fifths === null || fifths === undefined || fifths === 0) {
      return defaultNotes[chromaticIndex];
    }
    
    if (fifths < 0) {
      // Flat key signatures: use flats for affected notes
      const flatNotes = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
      const numFlats = Math.abs(fifths);
      
      // Order of flats: Bb, Eb, Ab, Db, Gb, Cb, Fb
      const flatOrder = [10, 3, 8, 1, 6, 11, 4]; // Chromatic indices for Bb, Eb, Ab, Db, Gb, Cb, Fb
      const affectedFlats = flatOrder.slice(0, numFlats);
      
      // Use flat spelling for affected notes
      if (affectedFlats.includes(chromaticIndex)) {
        return flatNotes[chromaticIndex];
      }
    } else if (fifths > 0) {
      // Sharp key signatures: use sharps for affected notes
      const sharpNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
      const numSharps = fifths;
      
      // Order of sharps: F#, C#, G#, D#, A#, E#, B#
      const sharpOrder = [6, 1, 8, 3, 10, 4, 11]; // Chromatic indices for F#, C#, G#, D#, A#, E#, B#
      const affectedSharps = sharpOrder.slice(0, numSharps);
      
      // Use sharp spelling for affected notes
      if (affectedSharps.includes(chromaticIndex)) {
        return sharpNotes[chromaticIndex];
      }
    }
    
    // Default to natural note names for unaffected notes
    return defaultNotes[chromaticIndex];
  }, []);

  // Helper function to convert note to display name
  const getNoteDisplayName = useCallback((sourceNote) => {
    if (!sourceNote?.Pitch) return '';

    const halfTone = sourceNote.halfTone;

    if (halfTone !== undefined) {
      // Account for active transposition (source notes keep their original pitch)
      const transpose = osmdRef.current?.Sheet?.Transpose || 0;

      // Get key signature fifths value, shifted along the circle of fifths when transposed
      let fifths = getKeySignatureFifths(sourceNote);
      if (fifths !== null && transpose !== 0) {
        fifths = ((fifths + transpose * 7 + 6) % 12 + 12) % 12 - 6;
      }

      const adjustedHalfTone = halfTone + transpose;
      const noteFromHalfTone = ((adjustedHalfTone % 12) + 12) % 12;
      const octaveFromHalfTone = Math.floor(adjustedHalfTone / 12);

      // Use key signature-aware spelling
      const noteName = getCorrectNoteSpelling(noteFromHalfTone, fifths);

      return noteName + octaveFromHalfTone.toString();
    }

    // Fallback if halfTone is not available
    return 'Unknown';
  }, [getKeySignatureFifths, getCorrectNoteSpelling]);

  // Clear existing debug rectangles
  const clearDebugRectangles = useCallback(() => {
    const svg = osmdContainerRef.current?.querySelector('svg');
    if (svg) {
      const existingRects = svg.querySelectorAll('.mouseplay-debug-rect');
      existingRects.forEach(rect => rect.remove());
    }
  }, []);

  // Clear existing note highlights
  const clearNoteHighlight = useCallback(() => {
    const svg = osmdContainerRef.current?.querySelector('svg');
    if (svg) {
      const existingHighlights = svg.querySelectorAll('.mouseplay-note-highlight');
      existingHighlights.forEach(highlight => highlight.remove());
    }
  }, []);

  // Create note highlight
  const createNoteHighlight = useCallback((graphicalNote) => {
    if (!graphicalNote?.PositionAndShape || !osmdContainerRef.current) return;
    
    clearNoteHighlight();
    
    const svg = osmdContainerRef.current.querySelector('svg');
    if (!svg) return;
    
    const notePos = graphicalNote.PositionAndShape.AbsolutePosition;
    const noteSize = graphicalNote.PositionAndShape.BoundingRectangle;
    
    // Create highlight circle
    const highlight = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    highlight.setAttribute('class', 'mouseplay-note-highlight');
    highlight.setAttribute('cx', notePos.x * 10);
    highlight.setAttribute('cy', notePos.y * 10);
    highlight.setAttribute('r', '8'); // Fixed radius for consistent highlighting
    highlight.setAttribute('fill', 'rgba(255, 215, 0, 0.4)'); // Gold color with transparency
    highlight.setAttribute('stroke', 'gold');
    highlight.setAttribute('stroke-width', '2');
    highlight.setAttribute('pointer-events', 'none');
    
    svg.appendChild(highlight);
  }, [clearNoteHighlight]);

  // Draw debug rectangles for all notes (disabled)
  const drawDebugRectangles = useCallback(() => {
    // Debug rectangles are now hidden
    return;
  }, []);

  // Helper function to check if a note is from a visible part
  const isNoteFromVisiblePart = useCallback((graphicalNote) => {
    if (!partControls || !graphicalNote?.sourceNote?.ParentStaff?.ParentInstrument) {
      return true; // Default to visible if we can't determine
    }
    
    const instrumentId = graphicalNote.sourceNote.ParentStaff.ParentInstrument.Id;
    
    // Match by id, not instrumentId
    const partControl = partControls.find(p => p.id === instrumentId);
    
    // If we can't find the part control, default to visible
    return partControl ? partControl.visible : true;
  }, [partControls]);

  // Throttle mouse move events to reduce spam
  const throttleRef = useRef(null);

  // Handle mouse movement for cursor changes in MousePlay mode
  const handleMouseMove = useCallback((event) => {
    // Throttle to reduce spam - only process every 50ms
    if (throttleRef.current) return;
    throttleRef.current = setTimeout(() => {
      throttleRef.current = null;
    }, 50);
    if (!(isMousePlayMode || cue) || !osmdRef.current?.graphic?.measureList) {
      setTooltip({ visible: false, x: 0, y: 0, note: '' });
      return;
    }

    const svg = osmdContainerRef.current?.querySelector('svg');
    if (!svg) return;

    // Get mouse coordinates relative to SVG
    const svgRect = svg.getBoundingClientRect();
    const clientX = event.clientX - svgRect.left;
    const clientY = event.clientY - svgRect.top;

    // Convert to SVG coordinate space
    const viewBox = svg.viewBox.baseVal;
    let svgX, svgY;

    if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
      svgX = (clientX / svgRect.width) * viewBox.width + viewBox.x;
      svgY = (clientY / svgRect.height) * viewBox.height + viewBox.y;
    } else {
      svgX = clientX;
      svgY = clientY;
    }

    // Convert to OSMD units
    const osmdX = svgX / 10;
    const osmdY = svgY / 10;

    // Check if mouse is over a note
    let overNote = false;
    let hoveredNote = null;
    let hoveredGraphicalNote = null;
    const tolerance = 2; // Smaller, more precise tolerance

    // Outside hover-to-hear only the cue's notes play
    if (!isMousePlayMode) {
      const graphic = osmdRef.current.graphic;
      const key = `${cue.part}|${cue.fromMeasure}`;
      if (cueCacheRef.current.key !== key || cueCacheRef.current.graphic !== graphic) {
        cueCacheRef.current = { key, graphic, notes: findCueNotes(graphic, cue.part, cue.fromMeasure) };
      }
      for (const graphicalNote of cueCacheRef.current.notes) {
        if (!isNoteFromVisiblePart(graphicalNote)) continue;
        const notePos = graphicalNote.PositionAndShape.AbsolutePosition;
        if (Math.hypot(osmdX - notePos.x, osmdY - notePos.y) < tolerance) {
          overNote = true;
          hoveredNote = graphicalNote.sourceNote;
          hoveredGraphicalNote = graphicalNote;
          break;
        }
      }
    }

    for (let measureIndex = 0; isMousePlayMode && measureIndex < osmdRef.current.graphic.measureList.length; measureIndex++) {
      const measureRow = osmdRef.current.graphic.measureList[measureIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const graphicalMeasure = measureRow[staffIndex];
        
        if (graphicalMeasure && graphicalMeasure.staffEntries) {
          for (let entryIndex = 0; entryIndex < graphicalMeasure.staffEntries.length; entryIndex++) {
            const staffEntry = graphicalMeasure.staffEntries[entryIndex];
            
            if (staffEntry?.graphicalVoiceEntries) {
              for (let voiceIndex = 0; voiceIndex < staffEntry.graphicalVoiceEntries.length; voiceIndex++) {
                const voiceEntry = staffEntry.graphicalVoiceEntries[voiceIndex];
                
                if (voiceEntry?.notes) {
                  for (let noteIndex = 0; noteIndex < voiceEntry.notes.length; noteIndex++) {
                    const graphicalNote = voiceEntry.notes[noteIndex];
                    
                    if (graphicalNote?.PositionAndShape && graphicalNote.sourceNote && !graphicalNote.sourceNote.isRestFlag && isNoteFromVisiblePart(graphicalNote)) {
                      const notePos = graphicalNote.PositionAndShape.AbsolutePosition;
                      const distance = Math.sqrt(
                        Math.pow(osmdX - notePos.x, 2) + Math.pow(osmdY - notePos.y, 2)
                      );
                      
                      if (distance < tolerance) {
                        overNote = true;
                        hoveredNote = graphicalNote.sourceNote;
                        hoveredGraphicalNote = graphicalNote;
                        break;
                      }
                    }
                  }
                  if (overNote) break;
                }
              }
              if (overNote) break;
            }
          }
          if (overNote) break;
        }
      }
      if (overNote) break;
    }

    // Update cursor and tooltip
    // (outside hover-to-hear, the score's own cursor is left alone elsewhere)
    svg.style.cursor = overNote ? 'pointer' : (isMousePlayMode ? 'default' : '');
    
    if (overNote && hoveredNote) {
      const noteName = getNoteDisplayName(hoveredNote);
      setTooltip({
        visible: true,
        x: event.clientX + 10,
        y: event.clientY - 30,
        note: noteName
      });
      
      // Highlight the hovered note
      createNoteHighlight(hoveredGraphicalNote);
      
      // Play the note on mouseover if it's different from the last played note
      // Create a more specific key using position to distinguish between same notes in close proximity
      const notePos = hoveredGraphicalNote?.PositionAndShape?.AbsolutePosition;
      const noteKey = `${hoveredNote.halfTone}_${hoveredNote.SourceMeasure?.MeasureNumber || 0}_${notePos?.x || 0}_${notePos?.y || 0}`;
      if (lastPlayedNoteRef.current !== noteKey && onPlayNote) {
        lastPlayedNoteRef.current = noteKey;
        onPlayNote(hoveredNote, 0, hoveredNote.SourceMeasure?.MeasureNumber || 0);
      }
    } else {
      setTooltip({ visible: false, x: 0, y: 0, note: '' });
      // Clear note highlight when not hovering over any note
      clearNoteHighlight();
      // Clear last played note when not hovering over any note
      lastPlayedNoteRef.current = null;
    }
  }, [isMousePlayMode, cue, getNoteDisplayName, onPlayNote, isNoteFromVisiblePart, createNoteHighlight, clearNoteHighlight]);

  // Leaving the score ends a hover
  const handleMouseLeave = useCallback(() => {
    const svg = osmdContainerRef.current?.querySelector('svg');
    if (svg && !isMousePlayMode) svg.style.cursor = '';
    setTooltip({ visible: false, x: 0, y: 0, note: '' });
    clearNoteHighlight();
    lastPlayedNoteRef.current = null;
  }, [isMousePlayMode, clearNoteHighlight]);

  // Reset cursor when MousePlay mode is disabled
  useEffect(() => {
    if (!isMousePlayMode && osmdContainerRef.current) {
      const svg = osmdContainerRef.current.querySelector('svg');
      if (svg) {
        svg.style.cursor = 'default';
      }
      clearDebugRectangles();
      clearNoteHighlight();
      setTooltip({ visible: false, x: 0, y: 0, note: '' });
    }
  }, [isMousePlayMode, cue, clearDebugRectangles, clearNoteHighlight]);

  // Draw debug rectangles when MousePlay mode is enabled
  useEffect(() => {
    if (isMousePlayMode) {
      // Small delay to ensure OSMD is ready
      const timeout = setTimeout(drawDebugRectangles, 100);
      return () => clearTimeout(timeout);
    }
  }, [isMousePlayMode, drawDebugRectangles]);

  // Clear last played note when part visibility changes
  useEffect(() => {
    if (isMousePlayMode) {
      // Reset note tracking when parts are shown/hidden to force recalculation
      lastPlayedNoteRef.current = null;
      setTooltip({ visible: false, x: 0, y: 0, note: '' });
      clearNoteHighlight();
    }
  }, [partControls, isMousePlayMode, clearNoteHighlight]);

  // Expose the container ref and render coordinator to parent component
  useImperativeHandle(ref, () => ({
    getContainer: () => osmdContainerRef.current,
    getOsmd: () => osmdRef.current,
    getRenderCoordinator: () => renderCoordinatorRef.current
  }));

  useEffect(() => {
    if (!osmdContainerRef.current) return;

    const isNewOsmd = !osmdRef.current;
    if (isNewOsmd) {
      osmdRef.current = new OpenSheetMusicDisplay(osmdContainerRef.current, {
        autoResize: true,
        backend: "svg",
        drawTitle: true,
      });

      // Enable transposition support (used via osmd.Sheet.Transpose)
      osmdRef.current.TransposeCalculator = new TransposeCalculator();
    }

    // The coordinator is destroyed on unmount (its osmdRef is nulled) while the
    // OSMD instance survives in its ref, so recreate it whenever it is missing —
    // otherwise a StrictMode remount leaves every coordinated render a no-op.
    if (!renderCoordinatorRef.current) {
      renderCoordinatorRef.current = new OSMDRenderCoordinator(osmdRef, {
        delay: 1500,
        onTimerUpdate: (state) => {
          setTimerState(state);
        },
        onRenderStart: () => {
          // Hide timer when render starts
          setTimerState(prev => ({ ...prev, isActive: false }));
        },
        onRenderComplete: (error) => {
          // Call legacy callback if it exists
          if (osmdRef.current?._onRenderComplete) {
            osmdRef.current._onRenderComplete();
            osmdRef.current._onRenderComplete = null;
          }
        }
      });
    }

    if (isNewOsmd && onOsmdReady) {
      onOsmdReady(osmdRef.current);
    }
  }, [onOsmdReady]);

  // Handle zoom changes
  useEffect(() => {
    if (!osmdRef.current || !renderCoordinatorRef.current) {
      return;
    }
    
    if (osmdRef.current.zoom === zoom) {
      return;
    }
    
    try {
      // Set zoom property
      osmdRef.current.zoom = zoom;
      
      // Mobile Safari workaround: For preset loading, use direct render to bypass coordinator issues
      const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isSettingsLoad = osmdRef.current?._isSettingsLoad;
      
      if (isMobileSafari && isSettingsLoad) {
        // NOTE: React state should already be updated by SettingsManager before this point
        // This is just the visual refresh fix for mobile Safari
        
        // Direct render with multiple strategies for mobile Safari (without clearing content)
        if (osmdRef.current && osmdRef.current.render) {
          osmdRef.current.render().then(() => {
            // Force DOM refresh with multiple strategies
            if (osmdRef.current && osmdRef.current.container) {
              const container = osmdRef.current.container;
              // Strategy 1: Hardware acceleration
              container.style.transform = 'translateZ(0)';
              setTimeout(() => {
                container.style.transform = '';
                
                // Strategy 2: Force another render with slight zoom nudge
                if (osmdRef.current && osmdRef.current.render) {
                  const originalZoom = osmdRef.current.zoom;
                  const nudgeZoom = originalZoom + 0.001;
                  osmdRef.current.zoom = nudgeZoom;
                  osmdRef.current.render().then(() => {
                    if (osmdRef.current) {
                      osmdRef.current.zoom = originalZoom;
                      osmdRef.current.render();
                    }
                  });
                }
              }, 10);
            }
          }).catch((error) => {
            console.error('Mobile Safari render error:', error);
          });
        }
      } else {
        // Use coordinator for normal operations
        renderCoordinatorRef.current.requestRender({ 
          priority: 'immediate', 
          reason: 'zoom-change' 
        });
      }
    } catch (error) {
      console.error('Zoom change error:', error);
    }
  }, [zoom]);

  // Cleanup render coordinator on unmount
  useEffect(() => {
    return () => {
      if (renderCoordinatorRef.current) {
        renderCoordinatorRef.current.destroy();
        renderCoordinatorRef.current = null;
      }
    };
  }, []);

  return (
    <div className="score-display-wrapper">
      {/* Render Timer */}
      <RenderTimer
        isActive={timerState.isActive}
        timeRemaining={timerState.timeRemaining}
        progress={timerState.progress}
        totalTime={1500}
      />


      <div 
        className="osmd-container" 
        ref={osmdContainerRef}
        onMouseMove={isMousePlayMode || cue ? handleMouseMove : undefined}
        onMouseLeave={isMousePlayMode || cue ? handleMouseLeave : undefined}
      ></div>

      {/* Note tooltip */}
      {tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 'bold',
            zIndex: 10000,
            pointerEvents: 'none',
            fontFamily: 'monospace'
          }}
        >
          {tooltip.note}
        </div>
      )}
    </div>
  );
});

ScoreDisplay.displayName = 'ScoreDisplay';

export default ScoreDisplay;